import React from 'react';
import {WordList} from '../../domain/types';
import WordListBadges from './WordListBadges';
import {
  CheckIcon,
  GlobeIcon,
  ListIcon,
  LockIcon,
  MinusIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from '../Icons';

export interface WordListRowActions {
  onSelect: (id: string) => void;
  onEdit: (id: string) => void;
  onTogglePublic: (id: string, isPublic: boolean) => void;
  onDelete: (id: string) => void;
  onRemoveFromCollection: (id: string) => void;
  onAddToCollection: (id: string) => void;
}

interface WordListRowProps extends WordListRowActions {
  list: WordList;
  /** `mine` shows edit/visibility/delete; `marketplace` shows Add, or that it is already added. */
  variant: 'mine' | 'marketplace';
  isSelected: boolean;
  isInCollection: boolean;
  isBusy: boolean;
}

const iconButton = 'p-1.5 rounded-lg text-gray-500 hover:text-gray-100 hover:bg-gray-800 '
  + 'transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed';

/** Marketplace rows carry a `wordCount`; owned ones carry the words themselves. */
export function wordCountOf(list: WordList): number {
  return list.wordCount ?? list.words?.length ?? 0;
}

/**
 * One word list. Selecting it fills the detail panel; everything else you can do to it is here.
 *
 * The name is the only clickable part of the row rather than the row itself — a row that both
 * selects on click and holds three destructive buttons is a row where every miss does something.
 */
const WordListRow: React.FC<WordListRowProps> = ({
  list,
  variant,
  isSelected,
  isInCollection,
  isBusy,
  onSelect,
  onEdit,
  onTogglePublic,
  onDelete,
  onRemoveFromCollection,
  onAddToCollection,
}) => {
  const count = wordCountOf(list);
  const isOwner = list.isOwner !== false;

  return (
    <div
      className={`group grid grid-cols-12 items-center gap-3 px-4 py-3 border-b border-gray-800/70
       last:border-b-0 transition-colors ${
    isSelected ? 'bg-indigo-500/[0.07]' : 'hover:bg-gray-800/30'
    }`}
    >
      <div className="col-span-12 md:col-span-6 min-w-0">
        <button
          type="button"
          onClick={() => onSelect(list.id)}
          aria-pressed={isSelected}
          className="flex w-full items-start gap-3 text-left cursor-pointer"
        >
          <ListIcon className={`w-4 h-4 mt-0.5 shrink-0 ${isSelected ? 'text-indigo-400' : 'text-gray-600'}`} />
          <span className="min-w-0">
            <span className="flex items-center gap-1.5 min-w-0">
              <span className="truncate text-sm font-medium text-gray-100" title={list.name}>
                {list.name}
              </span>
              <WordListBadges list={list} />
            </span>
            {list.description && (
              <span className="mt-0.5 block truncate text-xs text-gray-500" title={list.description}>
                {list.description}
              </span>
            )}
            {variant === 'marketplace' && list.creatorEmail && (
              <span className="mt-0.5 block truncate text-xs text-gray-600">
                by {list.creatorEmail}
              </span>
            )}
          </span>
        </button>
      </div>

      <div className="col-span-4 md:col-span-2 text-xs tabular-nums text-gray-400">
        {count} word{count === 1 ? '' : 's'}
      </div>

      <div className="col-span-8 md:col-span-4 flex items-center justify-end gap-1">
        {variant === 'marketplace' ? (
          isInCollection ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-2 py-0.5
             text-xs font-medium text-emerald-300 ring-1 ring-inset ring-emerald-500/30">
              <CheckIcon className="w-3 h-3" />
              In your lists
            </span>
          ) : (
            <button
              type="button"
              onClick={() => onAddToCollection(list.id)}
              disabled={isBusy}
              className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-2.5 py-1.5 text-xs
               font-medium text-white hover:bg-indigo-500 transition-colors
               disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              <PlusIcon className="w-3.5 h-3.5" />
              Add
            </button>
          )
        ) : isOwner ? (
          <>
            <button
              type="button"
              onClick={() => onTogglePublic(list.id, !list.isPublic)}
              disabled={isBusy}
              aria-label={list.isPublic ? `Make ${list.name} private` : `Make ${list.name} public`}
              title={list.isPublic ? 'Public — click to make it private' : 'Private — click to publish it'}
              className={`${iconButton} ${list.isPublic ? 'text-emerald-400 hover:text-emerald-300' : ''}`}
            >
              {list.isPublic
                ? <GlobeIcon className="w-4 h-4" />
                : <LockIcon className="w-4 h-4" />}
            </button>
            <button
              type="button"
              onClick={() => onEdit(list.id)}
              disabled={isBusy}
              aria-label={`Edit ${list.name}`}
              title="Edit this list"
              className={iconButton}
            >
              <PencilIcon className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => onDelete(list.id)}
              disabled={isBusy}
              aria-label={`Delete ${list.name}`}
              title="Delete this list"
              className={`${iconButton} hover:text-red-300 opacity-0 group-hover:opacity-100 focus:opacity-100`}
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => onRemoveFromCollection(list.id)}
            disabled={isBusy}
            title="Remove this list from your collection"
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-700 px-2.5 py-1.5
             text-xs font-medium text-gray-300 hover:border-gray-600 hover:bg-gray-800 transition-colors
             disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
          >
            <MinusIcon className="w-3.5 h-3.5" />
            Remove
          </button>
        )}
      </div>
    </div>
  );
};

export default WordListRow;
